// Auth store — Zustand with sessionStorage persistence.
//
// sessionStorage (not localStorage) is the right tradeoff for MVP:
// closing the tab clears the session, which matches the operational
// assumption that the founding cohort works on their own workstation
// and a session shouldn't outlive a browser session.
//
// We keep the access + refresh tokens, the decoded email + tenant_id
// from the access token, and a derived isAuthenticated flag. No token
// refresh loop in MVP — 15-minute access TTL + relogin is acceptable
// for the founding cohort (see SPEC §4.5 / the Phase 7 resume notes).

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { decodeJwtPayload } from "./api";

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  email: string | null;
  tenantId: number | null;
  role: string | null;
  // Provisioned clinical-authority claim (SPEC-ADDENDUM §A7 Phase 4). Gates
  // Provider authorship in the Preauthorization Writer. "none" when absent.
  clinicalAuthority: string | null;
  setSession: (access: string, refresh: string, email?: string) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

// JWT email isn't in the Phase 1 access token (just sub / tenant_id /
// role / typ / exp). We accept an explicit email from the login form
// to surface in the nav bar. tenant_id + role come from the token.
function decodeSession(access: string): {
  tenantId: number | null;
  role: string | null;
  clinicalAuthority: string | null;
} {
  const claims = decodeJwtPayload(access);
  if (!claims) return { tenantId: null, role: null, clinicalAuthority: null };
  return {
    tenantId: typeof claims.tenant_id === "number" ? claims.tenant_id : null,
    role: typeof claims.role === "string" ? claims.role : null,
    clinicalAuthority:
      typeof claims.clinical_authority === "string" ? claims.clinical_authority : null,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      email: null,
      tenantId: null,
      role: null,
      clinicalAuthority: null,
      setSession: (access, refresh, email) => {
        const decoded = decodeSession(access);
        set({
          accessToken: access,
          refreshToken: refresh,
          email: email ?? get().email,
          tenantId: decoded.tenantId,
          role: decoded.role,
          clinicalAuthority: decoded.clinicalAuthority,
        });
      },
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          email: null,
          tenantId: null,
          role: null,
          clinicalAuthority: null,
        }),
      isAuthenticated: () => get().accessToken !== null,
    }),
    {
      name: "caselens-auth",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
